/**
 * THE CORNER LANDING TRAIL — the arc, and the ring it lands in. (§4.8)
 *
 * Owner, 2026-08-09: *"in corner show a trail for the ball where its gonna land
 * like FIFA game"*.
 *
 * ── Why this is NOT in indicators.ts ────────────────────────────────────────
 *
 * `indicators.ts` is the file the deleted aim markers lived in, and
 * `indicators.test.ts` pins it at ZERO cones and ZERO polylines specifically so
 * a third ground marker cannot quietly reappear there. That test is right and
 * stays untouched: this is a different marker with a different justification,
 * and it declares itself as its own subsystem so the guard on that file keeps
 * doing its job.
 *
 * ── Why this one is allowed to exist when the 7 m one was deleted ───────────
 *
 * Because it is computed, not assumed. `indicators.ts`'s own note says what the
 * replacement would have to be:
 *
 *   > A real landing-spot predictor would need the sim's own ballistics (§3.2
 *   > drag + Magnus, and the strike error the sim rolls at release […])
 *
 * `ai/restart-preview.ts` → `sim/ball.ts previewPassLaunch` → `sim/ball-predict.ts
 * predictFlight` is that predictor: the same target selector, the same charge
 * rule, the same launch arithmetic and the same drag+Magnus integrator the sim
 * uses. Nothing on screen here is a tuned constant.
 *
 * On the parenthesis about strike error: it is now stale in production —
 * `STRIKE_AIM_ERROR_ENABLED` is `false` (constants.ts KICK DIRECTIVE), so the
 * launch IS knowable before the strike. If that is ever turned back on, the
 * preview reports `exact === false` and this file DIMS THE WHOLE TRAIL and drops
 * the landing ring, because a ring drawn around the centre of an error cone is
 * the 7 m marker's mistake wearing better maths.
 *
 * ── What it claims ─────────────────────────────────────────────────────────
 *
 * The AIM, live. The taker is still turning toward his target while he walks in,
 * so the arc sweeps with him — which is the FIFA behaviour the owner is asking
 * for, and it is honest because it is genuinely re-solved every frame from his
 * current facing. It is drawn only while the corner is being STAGED; the moment
 * the ball is really struck the trail vanishes and the real ball is the truth.
 *
 * Zero per-frame allocation (§15.7): geometry is allocated once at `init` at
 * full capacity and only `drawRange` moves.
 */

import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  DoubleSide,
  Line,
  LineBasicMaterial,
  Mesh,
  MeshBasicMaterial,
  RingGeometry,
  type Scene,
} from 'three';
import type { RenderContext, RenderState, RenderSubsystem } from './context.js';

/** Matches CORNER_TRAIL_HORIZON_S / CORNER_TRAIL_SAMPLE_TICKS in restart-preview.ts. */
const MAX_TRAIL_POINTS = 128;

/** Lifted off the turf so the arc's tail does not z-fight the pitch. */
const LIFT_Y = 0.04;

const TRAIL_COLOR = 0x6cf0ff;
const RING_COLOR = 0x6cf0ff;
const RING_INNER = 0.62;
const RING_OUTER = 0.82;
/** The ring breathes so it reads as live, at the same rate as the pass disc. */
const RING_PULSE_HZ = 2.4;
const RING_PULSE_DEPTH = 0.25;
const RING_BASE_OPACITY = 0.85;
const TRAIL_BASE_OPACITY = 0.8;
/** Applied to both when the sim would roll an error the preview cannot know. */
const INEXACT_DIM = 0.35;

export function createCornerTrailSystem(): RenderSubsystem {
  let line: Line | null = null;
  let ring: Mesh | null = null;
  let sceneRef: Scene | null = null;
  let positions: Float32Array | null = null;

  return {
    name: 'cornerTrail',

    init(ctx: RenderContext): void {
      sceneRef = ctx.scene;

      const geo = new BufferGeometry();
      positions = new Float32Array(MAX_TRAIL_POINTS * 3);
      geo.setAttribute('position', new BufferAttribute(positions, 3));
      geo.setDrawRange(0, 0);
      const mat = new LineBasicMaterial({
        color: TRAIL_COLOR,
        transparent: true,
        opacity: TRAIL_BASE_OPACITY,
        depthWrite: false,
        // Additive so the arc reads over both the turf and the crowd behind it
        // without a dark outline; it is a HUD element drawn in world space.
        blending: AdditiveBlending,
      });
      line = new Line(geo, mat);
      line.frustumCulled = false; // the arc leaves the taker's bounding sphere
      line.renderOrder = 3;
      line.visible = false;
      line.name = 'corner-trail-arc';
      sceneRef.add(line);

      const ringMat = new MeshBasicMaterial({
        color: RING_COLOR,
        transparent: true,
        opacity: RING_BASE_OPACITY,
        depthWrite: false,
        side: DoubleSide,
      });
      ring = new Mesh(new RingGeometry(RING_INNER, RING_OUTER, 48), ringMat);
      ring.rotation.x = -Math.PI / 2;
      ring.renderOrder = 3;
      ring.frustumCulled = false;
      ring.visible = false;
      ring.name = 'corner-trail-landing';
      sceneRef.add(ring);
    },

    update(state: RenderState): void {
      if (line === null || ring === null || positions === null) return;

      if (!state.cornerTrailShow || state.cornerTrailCount < 2) {
        line.visible = false;
        ring.visible = false;
        return;
      }

      const src = state.cornerTrailPoints;
      const n = Math.min(state.cornerTrailCount, MAX_TRAIL_POINTS);
      for (let i = 0; i < n; i++) {
        const o = i * 3;
        positions[o] = src[o]!;
        positions[o + 1] = src[o + 1]! + LIFT_Y;
        positions[o + 2] = src[o + 2]!;
      }
      const attr = line.geometry.getAttribute('position') as BufferAttribute;
      attr.needsUpdate = true;
      line.geometry.setDrawRange(0, n);
      line.visible = true;

      const dim = state.cornerTrailExact ? 1 : INEXACT_DIM;
      (line.material as LineBasicMaterial).opacity = TRAIL_BASE_OPACITY * dim;

      // THE LANDING RING. Drawn only when the prediction actually reached the
      // turf inside its horizon, and never when the launch is not exact — see
      // the header. `landed === false` means "I do not know yet", and the
      // honest picture of that is no ring at all.
      if (state.cornerTrailLanded && state.cornerTrailExact) {
        const pulse = 1 + Math.sin(state.elapsed * RING_PULSE_HZ * Math.PI * 2) * RING_PULSE_DEPTH;
        ring.position.set(state.cornerTrailLandX, LIFT_Y, state.cornerTrailLandZ);
        ring.scale.setScalar(pulse);
        (ring.material as MeshBasicMaterial).opacity = RING_BASE_OPACITY;
        ring.visible = true;
      } else {
        ring.visible = false;
      }
    },

    dispose(): void {
      if (sceneRef !== null) {
        if (line !== null) sceneRef.remove(line);
        if (ring !== null) sceneRef.remove(ring);
      }
      if (line !== null) {
        line.geometry.dispose();
        (line.material as LineBasicMaterial).dispose();
        line = null;
      }
      if (ring !== null) {
        ring.geometry.dispose();
        (ring.material as MeshBasicMaterial).dispose();
        ring = null;
      }
      positions = null;
      sceneRef = null;
    },
  };
}
